/**
 * recommendationGroups - clinical browse taxonomy for the F2 recommendations
 * picker.
 *
 * The `recommendations` library (migration 0089) is flat: each entry carries a
 * `topic` + `topic_slug` but no category column. The picker still wants the 35
 * topics grouped into legible, scannable clinical buckets, so the grouping is
 * derived here (client-side) keyed by `topic_slug`. This is tenant-NEUTRAL
 * clinical categorization - no Peace-staff names, no per-tenant content - and it
 * degrades gracefully: any slug not in the map (e.g. a future seeded topic, or a
 * scenario-split) falls into the trailing "More recommendations" bucket rather
 * than disappearing.
 *
 * Order of GROUP_ORDER is the display order. Within a group the picker sorts
 * topics alphabetically by their display name.
 */

export interface RecommendationGroup {
  /** Stable key (used for React keys + the PR3 "Suggested" group can't collide). */
  key: string;
  /** Human label shown as the group header. */
  label: string;
}

export const GROUP_ORDER: readonly RecommendationGroup[] = [
  { key: 'care-support', label: 'Care & support services' },
  { key: 'medical-clinical', label: 'Medical & clinical' },
  { key: 'safety-home', label: 'Safety & the home' },
  { key: 'legal-financial', label: 'Legal & financial planning' },
  { key: 'living-placement', label: 'Living arrangements & placement' },
] as const;

/** Trailing catch-all for any slug not mapped below. */
export const FALLBACK_GROUP: RecommendationGroup = {
  key: 'more',
  label: 'More recommendations',
};

/** topic_slug -> group key. The 35 seeded topics (migration 0089 §2). */
const SLUG_TO_GROUP: Readonly<Record<string, string>> = {
  // Care & support services
  'adult-day-programs': 'care-support',
  'home-health-aide': 'care-support',
  'ongoing-care-management-services': 'care-support',
  'support-groups-for-caregivers': 'care-support',
  'counseling-for-client': 'care-support',
  'spice-activity-program': 'care-support',
  'transportation-option': 'care-support',
  'meal-delivery-and-healthy-meal-services': 'care-support',
  'physical-therapy-tune-up': 'care-support',

  // Medical & clinical
  geriatrician: 'medical-clinical',
  'geriatric-psychiatrist': 'medical-clinical',
  'specialist-physician-referral': 'medical-clinical',
  'diagnostic-testing': 'medical-clinical',
  'medication-management': 'medical-clinical',
  constipation: 'medical-clinical',
  'pacemaker-defibrillator': 'medical-clinical',
  'alzheimer-s-and-other-dementias-clinical-studies': 'medical-clinical',

  // Safety & the home
  'home-safety-modifications': 'safety-home',
  'emergency-call-alert-pendant': 'safety-home',
  'medical-supply-and-equipment': 'safety-home',
  'driver-evaluation': 'safety-home',
  'hoarding-clean-out': 'safety-home',
  'calendar-assistance-orientation': 'safety-home',

  // Legal & financial planning
  'elder-law-attorney': 'legal-financial',
  'power-of-attorney-advance-directive': 'legal-financial',
  polst: 'legal-financial',
  guardianship: 'legal-financial',
  'medicaid-planning-and-application': 'legal-financial',
  'long-term-care-insurance-review': 'legal-financial',
  'medicare-supplement': 'legal-financial',
  'insurance-card-replacement': 'legal-financial',

  // Living arrangements & placement
  'continuing-care-retirement-community-ccrc': 'living-placement',
  'boarding-home-and-residential-health-care-facilities': 'living-placement',
  'skilled-nursing-facility': 'living-placement',
  hospice: 'living-placement',
};

/** Resolve a topic_slug to its group key (fallback bucket when unmapped). */
export function groupKeyForSlug(slug: string): string {
  return SLUG_TO_GROUP[slug] ?? FALLBACK_GROUP.key;
}
